import Autoplay from 'embla-carousel-autoplay';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { Children, isValidElement, ReactNode, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { company } from '~/company/Company';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { useDotButton } from '~/hooks/useDotButton';
import { useGlobal } from '~/hooks/useGlobal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';
import { userStore } from '~/stores';

import SkeletonListRows from '../Skeleton/SkeletonList';

import CarouselErrorBoundary from './CarouselErrorBoundary';
import CarouselIcon, { CarouselIconProps } from './CarouselIcon';
import styles from './CommonCarousel.module.scss';

type Props = {
  children: ReactNode;
  arrows?: boolean;
  spaceBetween?: number;
  slidesPerViewProp?: number | 'auto';
  pagination?: {
    enabled?: boolean;
    className?: string;
    bulletClassName?: string;
    activeBulletClassName?: string;
    clickable?: boolean;
    dynamicBullets?: boolean;
  };
  isAutoplay?: boolean;
  slideClassName?: string;
  sliderClassName?: string;
  skeletonClassName?: string;
  scrollAuto?: boolean;
  leftArrowClassName?: string;
  rightArrowClassName?: string;
  containerClassName?: string;
  skeletonComponent?: ReactNode;
  skeletonCount?: number;
  slideVariant?: CarouselIconProps['variant'];
  slidesOffsetAfter?: number;
  slidesOffsetBefore?: number;
  freeMode?: boolean;
  slidesPerGroup?: number;
};

const CommonCarouselComponent = ({
  children,
  spaceBetween = 20,
  slidesPerViewProp = 'auto',
  pagination,
  isAutoplay,
  slideClassName,
  sliderClassName,
  skeletonClassName,
  scrollAuto = false,
  leftArrowClassName,
  rightArrowClassName,
  containerClassName,
  skeletonComponent,
  skeletonCount,
  slideVariant = 'fill',
  arrows = true,
  slidesOffsetAfter = 0,
  slidesOffsetBefore = 0,
  freeMode = false,
  slidesPerGroup = 1,
}: Props) => {
  const { isMobile } = useGlobal();

  const {
    enabled: paginationEnabled = false,
    clickable: paginationClickable = false,
    className: paginationClassName,
    bulletClassName,
    activeBulletClassName,
  } = pagination || {};

  const [api, setApi] = useState<CarouselApi | undefined>(undefined);
  const childrenArray = Children.toArray(children);
  const childrenLength = childrenArray.length;

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  const slidesPerView = slidesPerViewProp === 'auto' ? 1 : slidesPerViewProp;

  const shouldLoop = useMemo(() => {
    if (isAutoplay) {
      return childrenLength > slidesPerView;
    }
    return false;
  }, [isAutoplay, childrenLength, slidesPerView]);

  const autoplayPlugin = useMemo(
    () =>
      isAutoplay
        ? Autoplay({
            delay: 5000,
            stopOnInteraction: false,
          })
        : undefined,
    [isAutoplay],
  );

  const carouselOpts = useMemo(
    () => ({
      loop: shouldLoop,
      align: 'start' as const,
      slidesToScroll: scrollAuto
        ? slidesPerViewProp === 'auto'
          ? 1
          : slidesPerViewProp
        : slidesPerGroup,
      dragFree: freeMode && isMobile,
      containScroll: 'trimSnaps' as const,
    }),
    [
      shouldLoop,
      scrollAuto,
      slidesPerGroup,
      freeMode,
      isMobile,
      slidesPerViewProp,
    ],
  );

  const plugins = useMemo(() => {
    const result = [];
    if (autoplayPlugin) {
      result.push(autoplayPlugin);
    }
    return result;
  }, [autoplayPlugin]);

  const showArrows = useMemo(
    () => arrows && !isMobile && childrenLength > slidesPerView,
    [arrows, isMobile, childrenLength, slidesPerView],
  );

  if (!children || childrenLength === 0) {
    return (
      <div
        className={styles.skeletonContainer}
        style={{ gap: `${spaceBetween || 20}px` }}
      >
        <SkeletonListRows rows={skeletonCount ?? 9}>
          {skeletonComponent ?? <Skeleton className={skeletonClassName} />}
        </SkeletonListRows>
      </div>
    );
  }

  return (
    <CarouselErrorBoundary>
      <div
        className={classes([
          styles.container,
          containerClassName,
          'banner-carousel carousel',
        ])}
      >
        {!prevBtnDisabled && showArrows && (
          <CarouselIcon
            position="left"
            variant={slideVariant}
            className={leftArrowClassName}
            onClick={onPrevButtonClick}
          />
        )}
        <Carousel
          setApi={setApi}
          opts={carouselOpts}
          plugins={plugins}
          className={classes([styles.commonCarousel, sliderClassName])}
          dir={userStore.dir}
          key={userStore.dir}
        >
          <CarouselContent
            style={{
              paddingLeft: `${slidesOffsetBefore}px`,
              paddingRight: `${slidesOffsetAfter}px`,
            }}
          >
            {childrenArray.map((child, id) => {
              const key =
                isValidElement(child) &&
                child.key !== null &&
                child.key !== undefined
                  ? child.key
                  : id;

              return (
                <CarouselItem
                  key={key}
                  data-company={company.name}
                  className={slideClassName}
                  style={{
                    marginRight: `${spaceBetween}px`,
                    ...(slidesPerViewProp === 'auto'
                      ? undefined
                      : {
                          flexBasis: `calc((100% - ${
                            spaceBetween * (slidesPerView - 1)
                          }px) / ${slidesPerView})`,
                        }),
                  }}
                >
                  {child}
                </CarouselItem>
              );
            })}
          </CarouselContent>
          {paginationEnabled && scrollSnaps.length > 1 && (
            <div
              className={classes([
                paginationClassName || styles.pagination,
                paginationClickable && styles.clickable,
              ])}
            >
              {scrollSnaps.map((_, index) => (
                <span
                  key={index}
                  onClick={() => paginationClickable && onDotButtonClick(index)}
                  className={classes([
                    styles.paginationDot,
                    bulletClassName,
                    index === selectedIndex &&
                      classes([styles.paginationDot, activeBulletClassName]),
                  ])}
                />
              ))}
            </div>
          )}
        </Carousel>
        {!nextBtnDisabled && showArrows && (
          <CarouselIcon
            position="right"
            variant={slideVariant}
            className={rightArrowClassName}
            onClick={onNextButtonClick}
          />
        )}
      </div>
    </CarouselErrorBoundary>
  );
};

export default observer(CommonCarouselComponent);
